<template>
  <div class="performance__chart-container">
    <PerformanceChart
      :chart-node="chartNode"
      :key="chartNodeKey"
      @deleteChart="$emit('deleteChart')"
    />
  </div>
</template>

<script>
import PerformanceChart from './PerformanceChart.vue';

// Helper
import { performanceSimpleScoreChart as performanceChartNode } from '@/components/Reports/Utils/Library/Performance/performanceNodes.js';
import { PigeonDocNodeModel } from '@/pigeondoc/wrapper';

export default {
  name: 'PerformanceCharts',
  props: {
    chartData: {
      type: Array,
      default: () => [],
    },
    savedThreshold: {
      default: () => -1,
      type: Number,
    },
    isSegmented: {
      default: () => '',
      type: String,
    },
    isThematic: {
      default: () => false,
      type: Boolean,
    },
    minimalMode: {
      default: () => false,
      type: Boolean,
    },
    alternative_x: {
      default: () => '',
      type: String,
    },
    alternative_y: {
      default: () => '',
      type: String,
    },
    alternative_x_hover: {
      default: () => '',
      type: String,
    },
    alternative_y_hover: {
      default: () => '',
      type: String,
    },
    filtered_by: {
      default: () => '',
      type: String,
    },
    performanceMode: {
      default: () => 'means',
      type: String,
    },
    sentimentMode: {
      default: () => '',
      type: String,
    },
  },
  components: {
    PerformanceChart,
  },
  data() {
    return {
      chartNode: null,
      chartNodeKey: 0,
      error: false,
      errorMsg: null,
    };
  },
  watch: {
    chartData: {
      immediate: true,
      handler() {
        if (this.chartData && this.chartData.err) {
          this.error = true;
          this.errorMsg = 'Error: There was an error with performance analysis. Please try again.';
        } else if (this.chartData) {
          this.error = false;
          this.errorMsg = null;

          let chartType = 'HorizontalBarChart';
          let isSentimentAnaylsis = false;
          let alternative_x = this.alternative_x;
          let alternative_y = this.alternative_y;
          let alternative_x_hover = this.alternative_x_hover;
          let alternative_y_hover = this.alternative_y_hover;
          let hasTimeSeries = false;

          let thisTitle = 'Question scores';
          if (this.isThematic) {
            thisTitle = 'Theme coverage';
            if (this.filtered_by) {
              thisTitle = 'Theme coverage filtered by: ' + this.filtered_by;
            }
          }
          if (this.isSegmented && this.isSegmented !== '') {
            if (this.chartData.length == 1 && this.chartData[0].label) {
              thisTitle = this.chartData[0].label;
            }
            thisTitle = thisTitle + ' - Segmented by ' + this.isSegmented;
            alternative_y = 'Segments';
            alternative_x = 'blank';
            if (this.filtered_by) {
              thisTitle = 'Theme coverage filtered by: ' + this.filtered_by;
            }
          }
          let suffix = '';
          if (this.isThematic) {
            suffix = '%';
          }
          if (this.performanceMode == 'Volumes') {
            thisTitle = 'Question volumes';
          }

          for (var i = 0; i < this.chartData.length; i++) {
            if (this.chartData[i]['time_series_scores']) {
              chartType = 'HorizontalLineChart';
              hasTimeSeries = true;
              if (this.performanceMode == 'Volumes') {
                alternative_y = 'volumes';
                alternative_y_hover = 'performanceVolumesAverage';
                alternative_x = 'blank';
              }
              if (this.performanceMode == 'Means/averages') {
                alternative_y = 'performanceScore';
                alternative_y_hover = 'performanceScoreAverage';
                alternative_x = 'blank';
              }
            }
            // A fairly hacky way to detect if time series are nestled inside sentiment charts // TODO as this is used by simplePerformanceChart too, consider creating a utility
            if (this.chartData[i]['unique_sentiments_count']) {
              isSentimentAnaylsis = true;

              if (this.chartData[i]['unique_sentiments_count']['data']) {
                for (
                  var x = 0;
                  x < this.chartData[i]['unique_sentiments_count']['data'].length;
                  x++
                ) {
                  if (
                    this.chartData[i]['unique_sentiments_count']['data'][x][7] &&
                    this.chartData[i]['unique_sentiments_count']['data'][x][7].length
                  ) {
                    chartType = 'HorizontalLineChart';
                    hasTimeSeries = true;
                  }
                }
              }

              alternative_y = 'sentimentCategories';

              if (this.sentimentMode == 'Single score') {
                alternative_y_hover = 'categoricalSentimentCompound';
                alternative_x_hover = 'categoricalSentimentCompound';
                alternative_x = 'sentimentCompounds';
              } else if (this.sentimentMode == '5-point scale') {
                alternative_x = 'sentimentCount';
                alternative_y_hover = 'categoricalSentiment5point';
                alternative_x_hover = 'categoricalSentiment5pointCount';
              }
              if (hasTimeSeries) {
                alternative_x = 'blank';
                alternative_y = 'sentimentCompounds';
                alternative_y_hover = 'categoricalSentimentCompound';
              }
            }
            // A fairly hacky way to detect if time series are nestled inside segment charts
            if (this.chartData[i]['unique_segments_count_dict']) {
              for (var x = 0; x < this.chartData[i]['unique_segments_count_dict'].length; x++) {
                if (
                  this.chartData[i]['unique_segments_count_dict'][x]['time_series_scores'] &&
                  this.chartData[i]['unique_segments_count_dict'][x]['time_series_scores'].length
                ) {
                  chartType = 'HorizontalLineChart';

                  hasTimeSeries = true;
                  if (this.isSegmented) {
                    alternative_y_hover = 'Segment scores for ' + this.isSegmented;
                  }
                  alternative_y = 'Segment scores';
                }
              }
            }
            // A fairly hacky way to detect if time series are nestled inside segment charts but also inside a client_question_analysis object (such as from text themes)
            if (
              this.chartData[i]['client_question_analysis'] &&
              this.chartData[i]['client_question_analysis']['unique_segments_count_dict']
            ) {
              for (
                var x = 0;
                x <
                this.chartData[i]['client_question_analysis']['unique_segments_count_dict'].length;
                x++
              ) {
                if (
                  this.chartData[i]['client_question_analysis']['unique_segments_count_dict'][x][
                    'time_series_scores'
                  ] &&
                  this.chartData[i]['client_question_analysis']['unique_segments_count_dict'][x][
                    'time_series_scores'
                  ].length
                ) {
                  chartType = 'HorizontalLineChart';

                  hasTimeSeries = true;
                  if (this.chartData[i].label) {
                    alternative_y_hover = 'Segment scores for ' + this.chartData[i].label;
                  }
                  alternative_y = 'Themes by segment';
                }
              }
            }
          }

          if (isSentimentAnaylsis) {
            thisTitle = 'Sentiments across categories';
          }
          this.chartNode = new PigeonDocNodeModel(
            performanceChartNode(
              thisTitle,
              this.chartData,
              this.language,
              this.savedThreshold,
              chartType,
              alternative_x,
              alternative_y,
              alternative_x_hover,
              alternative_y_hover,
              suffix,
              this.performanceMode,
              this.sentimentMode,
              this.minimalMode,
            ),
          );

          this.chartNodeKey += 1;
        }
      },
    },
  },
  computed: {
    language() {
      return this.$store.getters['getLanguage'];
    },
  },
};
</script>

<style></style>
